import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Navigation from '@/components/feature/Navigation';
import Footer from '@/components/feature/Footer';
import bookingService, { type RoomBooking } from '@/services/bookingService';
import { usePropertyProfile } from '@/hooks/usePropertyProfile';
import { formatDateLong } from '@/lib/datetime';
import { requiredString, emailSchema, phoneSchema, optionalString } from '@/lib/validation/common';

const editBookingSchema = z.object({
  guestName: requiredString('Full name'),
  guestEmail: emailSchema,
  guestPhone: phoneSchema,
  checkIn: z.string().min(1, 'Check-in date is required'),
  checkOut: z.string().min(1, 'Check-out date is required'),
  checkInTime: z.string(),
  checkOutTime: z.string(),
  guests: z.coerce.number().int().min(1).max(10),
  specialRequests: optionalString(500),
});
type EditBookingValues = z.infer<typeof editBookingSchema>;

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error' | 'info';
}

const ManageBookingPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const printRef = useRef<HTMLDivElement>(null);
  const property = usePropertyProfile();

  const [bookingRef, setBookingRef] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState<RoomBooking | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);

  const [isEditing, setIsEditing] = useState(false);
  const editForm = useForm<EditBookingValues>({
    resolver: zodResolver(editBookingSchema),
    defaultValues: {
      guestName: '', guestEmail: '', guestPhone: '', checkIn: '', checkOut: '',
      checkInTime: '', checkOutTime: '', guests: 1, specialRequests: '',
    },
  });

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [autoSearched, setAutoSearched] = useState(false);

  // Auto-fill from URL params (e.g. redirected from booking success page)
  useEffect(() => {
    const refParam = searchParams.get('ref');
    const emailParam = searchParams.get('email');

    if (refParam && emailParam && !autoSearched) {
      setBookingRef(refParam.trim().toUpperCase());
      setEmail(emailParam.trim());
      setAutoSearched(true);
    }
  }, [searchParams, autoSearched]);

  // Auto-trigger search when URL params pre-fill both fields
  useEffect(() => {
    if (!autoSearched || !bookingRef || !email) return;

    const doSearch = async () => {
      setLoading(true);
      setBooking(null);
      setIsEditing(false);

      try {
        const found = await bookingService.getBookingByConfirmation(bookingRef, email);
        if (!found) {
          addToast('No booking found with that reference number. Please check and try again.', 'error');
          setLoading(false);
          return;
        }
        if (found.guest_email.toLowerCase() !== email.toLowerCase()) {
          addToast('The email address does not match this booking. Please check and try again.', 'error');
          setLoading(false);
          return;
        }

        setBooking(found);
        editForm.reset({
          guestName: found.guest_name || '',
          guestEmail: found.guest_email || '',
          guestPhone: found.guest_phone || '',
          checkIn: found.check_in || '',
          checkOut: found.check_out || '',
          checkInTime: found.check_in_time || '12:00',
          checkOutTime: found.check_out_time || '12:00',
          guests: found.guests || 1,
          specialRequests: found.special_requests || '',
        });
        addToast('Booking found successfully!', 'success');
      } catch (err) {
        console.error(err);
        addToast('Something went wrong while searching. Please try again.', 'error');
      } finally {
        setLoading(false);
      }
    };

    doSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSearched]);

  const addToast = (message: string, type: Toast['type']) => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setBooking(null);
    setIsEditing(false);

    try {
      const found = await bookingService.getBookingByConfirmation(bookingRef.trim().toUpperCase(), email.trim());

      if (!found) {
        addToast('No booking found with that reference number. Please check and try again.', 'error');
        setLoading(false);
        return;
      }

      if (found.guest_email.toLowerCase() !== email.trim().toLowerCase()) {
        addToast('The email address does not match this booking. Please check and try again.', 'error');
        setLoading(false);
        return;
      }

      setBooking(found);
      editForm.reset({
        guestName: found.guest_name || '',
        guestEmail: found.guest_email || '',
        guestPhone: found.guest_phone || '',
        checkIn: found.check_in || '',
        checkOut: found.check_out || '',
        checkInTime: found.check_in_time || '12:00',
        checkOutTime: found.check_out_time || '12:00',
        guests: found.guests || 1,
        specialRequests: found.special_requests || '',
      });
      addToast('Booking found successfully!', 'success');
    } catch (err) {
      console.error(err);
      addToast('Something went wrong while searching. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = editForm.handleSubmit(async (values) => {
    if (!booking) return;
    setLoading(true);

    try {
      const updated = await bookingService.updateBookingDetails(booking.id, {
        guestName: values.guestName,
        guestEmail: values.guestEmail,
        guestPhone: values.guestPhone,
        checkIn: values.checkIn,
        checkOut: values.checkOut,
        checkInTime: values.checkInTime,
        checkOutTime: values.checkOutTime,
        guests: values.guests,
        specialRequests: values.specialRequests,
      });

      if (updated) {
        setBooking(updated);
        setIsEditing(false);
        addToast('Booking updated successfully!', 'success');
      } else {
        addToast('Failed to update booking. Please try again.', 'error');
      }
    } catch (err) {
      console.error(err);
      addToast('An error occurred while updating.', 'error');
    } finally {
      setLoading(false);
    }
  });

  const handleCancel = async () => {
    if (!booking) return;
    setCancelLoading(true);

    try {
      await bookingService.cancelBooking(booking.id, booking.assigned_room_id, email.trim());
      addToast('Booking cancelled successfully.', 'success');
      setShowCancelModal(false);
      setBooking(null);
      setBookingRef('');
      setEmail('');
    } catch (err) {
      console.error(err);
      addToast('Failed to cancel booking. Please contact front desk.', 'error');
    } finally {
      setCancelLoading(false);
    }
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <title>Booking Confirmation - ${booking?.confirmation_number || ''}</title>
          <style>
            body { font-family: 'Open Sans', sans-serif; padding: 40px; color: #1a1a1a; }
            h1 { font-family: 'Montserrat', sans-serif; font-size: 24px; margin-bottom: 8px; }
            .section { margin-bottom: 24px; border-bottom: 1px solid #eee; padding-bottom: 16px; }
            .section h3 { font-family: 'Montserrat', sans-serif; font-size: 14px; text-transform: uppercase; color: #666; margin-bottom: 12px; letter-spacing: 0.5px; }
            .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 24px; }
            .label { font-size: 12px; color: #888; margin-bottom: 2px; }
            .value { font-size: 14px; font-weight: 600; }
            .status { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; }
            .confirmed { background: #dcfce7; color: #166534; }
            .pending { background: #fef9c3; color: #854d0e; }
            .cancelled { background: #fee2e2; color: #991b1b; }
            .checked-in { background: #dbeafe; color: #1e40af; }
            .logo { font-size: 18px; font-weight: 700; color: #002568; margin-bottom: 24px; }
          </style>
        </head>
        <body>
          <div class="logo">${property?.name ?? 'Guest House'}</div>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  // Via parseISO, not `new Date`: a date-only string like "2026-08-01" parses
  // as UTC midnight and then renders in local time, so every guest west of UTC
  // saw their check-in a day early.
  const formatDate = (dateStr: string) => formatDateLong(dateStr, 'N/A');

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
    }).format(price || 0);
  };

  const getStatusBadge = (status: string) => {
    const map: Record<string, string> = {
      confirmed: 'bg-green-100 text-green-800',
      'checked-in': 'bg-babcock-navy-100 text-babcock-navy-800',
      'checked-out': 'bg-gray-100 text-gray-700',
      pending: 'bg-yellow-100 text-yellow-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return map[status] || 'bg-gray-100 text-gray-700';
  };

  const getPaymentBadge = (status: string) => {
    const map: Record<string, string> = {
      paid: 'bg-green-100 text-green-800',
      partial: 'bg-yellow-100 text-yellow-800',
      pending: 'bg-red-100 text-red-800',
      refunded: 'bg-gray-100 text-gray-700',
    };
    return map[status] || 'bg-gray-100 text-gray-700';
  };

  const canEdit = booking && !['cancelled', 'checked-out'].includes(booking.status);
  const canCancel = booking && !['cancelled', 'checked-out', 'checked-in'].includes(booking.status);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      {/* Toasts */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`px-4 py-3 rounded-lg shadow-lg text-sm font-medium animate-fadeIn ${
              toast.type === 'success'
                ? 'bg-green-600 text-white'
                : toast.type === 'error'
                  ? 'bg-red-600 text-white'
                  : 'bg-babcock-navy-700 text-white'
            }`}
          >
            <div className="flex items-center gap-2">
              <i
                className={`${
                  toast.type === 'success'
                    ? 'ri-check-line'
                    : toast.type === 'error'
                      ? 'ri-error-warning-line'
                      : 'ri-information-line'
                } text-lg`}
              />
              {toast.message}
            </div>
          </div>
        ))}
      </div>

      <div className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Page Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2 font-heading">Manage Your Booking</h1>
          <p className="text-gray-600 text-sm">Enter your booking reference and email to view or modify your reservation</p>
        </div>

        {/* Search Form */}
        <div className="max-w-md mx-auto bg-white rounded-xl p-6 mb-8">
          <form onSubmit={handleSearch} className="space-y-4">
            <div>
              <label htmlFor="bookingRef" className="block text-sm font-medium text-gray-700 mb-1">
                Booking Reference
              </label>
              <input
                type="text"
                id="bookingRef"
                value={bookingRef}
                onChange={(e) => setBookingRef(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-babcock-navy-400 focus:border-transparent text-sm"
                placeholder="e.g. BGH20261234"
                required
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-babcock-navy-400 focus:border-transparent text-sm"
                placeholder="Enter the email used for booking"
                required
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-babcock-navy-700 text-white py-2.5 px-4 rounded-lg hover:bg-babcock-navy-800 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <i className="ri-loader-4-line animate-spin mr-2" />
                  Searching...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <i className="ri-search-line mr-2" />
                  Find Booking
                </span>
              )}
            </button>
          </form>
        </div>

        {/* Booking Details */}
        {booking && (
          <div ref={printRef} className="space-y-6 animate-fadeIn">
            {/* Status Banner */}
            <div className="bg-white rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-babcock-navy-100 flex items-center justify-center">
                  <i className="ri-hotel-bed-line text-babcock-navy-700 text-xl" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Confirmation</p>
                  <p className="text-lg font-bold text-gray-900 font-heading">{booking.confirmation_number}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1.5 rounded-full text-xs font-semibold capitalize ${getStatusBadge(booking.status)}`}>
                  {booking.status.replace('-', ' ')}
                </span>
                <span className={`px-3 py-1.5 rounded-full text-xs font-semibold capitalize ${getPaymentBadge(booking.payment_status)}`}>
                  {booking.payment_status}
                </span>
              </div>
            </div>

            {/* Action Bar */}
            <div className="flex flex-wrap gap-3">
              {canEdit && (
                <button
                  onClick={() => setIsEditing(!isEditing)}
                  disabled={loading}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 ${
                    isEditing
                      ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      : 'bg-babcock-navy-700 text-white hover:bg-babcock-navy-800'
                  }`}
                >
                  <i className={`${isEditing ? 'ri-close-line' : 'ri-edit-line'} mr-1.5`} />
                  {isEditing ? 'Cancel Editing' : 'Edit Booking'}
                </button>
              )}
              <button
                onClick={handlePrint}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-printer-line mr-1.5" />
                Print Slip
              </button>
              {canCancel && (
                <button
                  onClick={() => setShowCancelModal(true)}
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-red-50 border border-red-200 text-red-700 hover:bg-red-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-close-circle-line mr-1.5" />
                  Cancel Booking
                </button>
              )}
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Guest Information */}
              <div className="bg-white rounded-xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <i className="ri-user-line text-babcock-navy-600" />
                  <h3 className="font-semibold text-gray-900 font-heading">Guest Information</h3>
                </div>
                {isEditing ? (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Full Name</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-babcock-navy-400"
                        {...editForm.register('guestName')}
                      />
                      {editForm.formState.errors.guestName && (
                        <p className="mt-1 text-xs text-red-600">{editForm.formState.errors.guestName.message}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                      <input
                        type="email"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-babcock-navy-400"
                        {...editForm.register('guestEmail')}
                      />
                      {editForm.formState.errors.guestEmail && (
                        <p className="mt-1 text-xs text-red-600">{editForm.formState.errors.guestEmail.message}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
                      <input
                        type="tel"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-babcock-navy-400"
                        {...editForm.register('guestPhone')}
                      />
                      {editForm.formState.errors.guestPhone && (
                        <p className="mt-1 text-xs text-red-600">{editForm.formState.errors.guestPhone.message}</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm text-gray-500">Name</span>
                      <span className="text-sm font-medium text-gray-900">{booking.guest_name}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm text-gray-500">Email</span>
                      <span className="text-sm font-medium text-gray-900">{booking.guest_email}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm text-gray-500">Phone</span>
                      <span className="text-sm font-medium text-gray-900">{booking.guest_phone || 'Not provided'}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Stay Details */}
              <div className="bg-white rounded-xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <i className="ri-calendar-line text-babcock-navy-600" />
                  <h3 className="font-semibold text-gray-900 font-heading">Stay Details</h3>
                </div>
                {isEditing ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Check-in Date</label>
                        <input
                          type="date"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-babcock-navy-400"
                          {...editForm.register('checkIn')}
                        />
                        {editForm.formState.errors.checkIn && (
                          <p className="mt-1 text-xs text-red-600">{editForm.formState.errors.checkIn.message}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Check-out Date</label>
                        <input
                          type="date"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-babcock-navy-400"
                          {...editForm.register('checkOut')}
                        />
                        {editForm.formState.errors.checkOut && (
                          <p className="mt-1 text-xs text-red-600">{editForm.formState.errors.checkOut.message}</p>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Check-in Time</label>
                        <input
                          type="time"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-babcock-navy-400"
                          {...editForm.register('checkInTime')}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Check-out Time</label>
                        <input
                          type="time"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-babcock-navy-400"
                          {...editForm.register('checkOutTime')}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Number of Guests</label>
                      <input
                        type="number"
                        min={1}
                        max={10}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-babcock-navy-400"
                        {...editForm.register('guests', { valueAsNumber: true })}
                      />
                      {editForm.formState.errors.guests && (
                        <p className="mt-1 text-xs text-red-600">{editForm.formState.errors.guests.message}</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm text-gray-500">Check-in</span>
                      <span className="text-sm font-medium text-gray-900">
                        {formatDate(booking.check_in)} at {booking.check_in_time || '12:00'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm text-gray-500">Check-out</span>
                      <span className="text-sm font-medium text-gray-900">
                        {formatDate(booking.check_out)} at {booking.check_out_time || '12:00'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm text-gray-500">Nights</span>
                      <span className="text-sm font-medium text-gray-900">{booking.nights}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm text-gray-500">Guests</span>
                      <span className="text-sm font-medium text-gray-900">{booking.guests}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Room Information */}
              <div className="bg-white rounded-xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <i className="ri-door-open-line text-babcock-navy-600" />
                  <h3 className="font-semibold text-gray-900 font-heading">Room Information</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-500">Room Type</span>
                    <span className="text-sm font-medium text-gray-900">{booking.room_type_name}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-500">Room Number</span>
                    <span className="text-sm font-medium text-gray-900">
                      {booking.room_number || 'Not yet assigned'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-500">Extended Checkout</span>
                    <span className="text-sm font-medium text-gray-900">
                      {booking.extended_checkout ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Payment Information */}
              <div className="bg-white rounded-xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <i className="ri-bank-card-line text-babcock-navy-600" />
                  <h3 className="font-semibold text-gray-900 font-heading">Payment</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-500">Total Amount</span>
                    <span className="text-lg font-bold text-gray-900">{formatPrice(booking.total_amount)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-500">Payment Status</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${getPaymentBadge(booking.payment_status)}`}>
                      {booking.payment_status}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-500">Booked On</span>
                    <span className="text-sm font-medium text-gray-900">
                      {new Date(booking.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Special Requests */}
            <div className="bg-white rounded-xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-chat-1-line text-babcock-navy-600" />
                <h3 className="font-semibold text-gray-900 font-heading">Special Requests</h3>
              </div>
              {isEditing ? (
                <textarea
                  rows={3}
                  maxLength={500}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-babcock-navy-400 resize-none"
                  placeholder="Any special requests for your stay..."
                  {...editForm.register('specialRequests')}
                />
              ) : (
                <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-4">
                  {booking.special_requests || 'No special requests provided.'}
                </p>
              )}
            </div>

            {/* Edit Save/Cancel */}
            {isEditing && (
              <div className="flex gap-3">
                <button
                  onClick={handleUpdate}
                  disabled={loading}
                  className="flex-1 bg-babcock-navy-700 text-white py-2.5 px-4 rounded-lg hover:bg-babcock-navy-800 transition-colors text-sm font-medium cursor-pointer disabled:opacity-50 whitespace-nowrap"
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <i className="ri-loader-4-line animate-spin mr-2" />
                      Saving...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center">
                      <i className="ri-save-line mr-2" />
                      Save Changes
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setIsEditing(false)}
                  disabled={loading}
                  className="flex-1 bg-gray-100 text-gray-700 py-2.5 px-4 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium cursor-pointer disabled:opacity-50 whitespace-nowrap"
                >
                  <i className="ri-close-line mr-2" />
                  Discard
                </button>
              </div>
            )}

            {/* Need Help */}
            <div className="bg-babcock-navy-50 rounded-xl p-6 text-center">
              <p className="text-sm text-gray-700 mb-2">Need help with your booking?</p>
              <p className="text-xs text-gray-500 mb-3">Contact our front desk for assistance with modifications or enquiries.</p>
              <button
                onClick={() => navigate('/back-office/front-office')}
                className="text-sm text-babcock-navy-700 font-medium hover:underline cursor-pointer"
              >
                Visit Front Office Dashboard
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Cancel Confirmation Modal */}
      {showCancelModal && booking && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full p-6 animate-fadeIn">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-alert-line text-red-600 text-xl" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 text-center mb-2 font-heading">Cancel Booking?</h3>
            <p className="text-sm text-gray-600 text-center mb-6">
              You are about to cancel booking <strong>{booking.confirmation_number}</strong>. This will release your room back to inventory. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowCancelModal(false)}
                disabled={cancelLoading}
                className="flex-1 py-2.5 px-4 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors text-sm font-medium cursor-pointer disabled:opacity-50 whitespace-nowrap"
              >
                Keep Booking
              </button>
              <button
                onClick={handleCancel}
                disabled={cancelLoading}
                className="flex-1 py-2.5 px-4 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors text-sm font-medium cursor-pointer disabled:opacity-50 whitespace-nowrap"
              >
                {cancelLoading ? (
                  <span className="flex items-center justify-center">
                    <i className="ri-loader-4-line animate-spin mr-2" />
                    Cancelling...
                  </span>
                ) : (
                  'Yes, Cancel'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ManageBookingPage;
